import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close } from '@mui/icons-material';
import { productService } from '../services/productService';
import { partService } from '../services/partService';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';

const emptyProductForm = () => ({
  name: '',
  price: '',
  quantity_on_hand: '',
  parts: [] // [{ part_id, quantity, part_number?, part_name? }]
});

const Products = () => {
  const DEFAULT_COL_WIDTHS = { name: 220, price: 110, qtyOnHand: 110, parts: 260 };
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyProductForm());
  const [saving, setSaving] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_products_col_widths',
    minWidths: { name: 140, price: 90, qtyOnHand: 90, parts: 140 }
  });

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { search: searchTerm || undefined, limit: 200 };
      const res = await productService.getAll(params);
      const list = res.data ?? [];
      setProducts(list);
      setSelectedProduct(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadParts = async () => {
    try {
      const res = await partService.getAll({ limit: 500, activeOnly: 'false' });
      setParts(res.data ?? []);
    } catch (err) {
      console.error('Error loading parts:', err);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    loadParts();
  }, []);

  const handleSearch = () => {
    loadProducts();
  };

  const openNewDialog = () => {
    setFormData(emptyProductForm());
    setSelectedProduct(null);
    setDialogOpen(true);
    setError('');
  };

  const openEditDialog = async (row) => {
    if (!row?.id) return;
    try {
      const res = await productService.getById(row.id);
      const data = res.data ?? res;
      const partsList = (data.parts || []).map((p) => ({
        part_id: p.part_id,
        quantity: p.quantity ?? 1,
        part_number: p.part_number,
        part_name: p.part_name
      }));
      setFormData({
        name: data.name ?? '',
        price: data.price != null ? String(data.price) : '',
        quantity_on_hand: data.quantity_on_hand != null ? String(data.quantity_on_hand) : '',
        parts: partsList.length ? partsList : [{ part_id: '', quantity: 1 }]
      });
      setSelectedProduct(row);
      setDialogOpen(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load product');
    }
  };

  const addPartRow = () => {
    setFormData((f) => ({
      ...f,
      parts: [...f.parts, { part_id: '', quantity: 1 }]
    }));
  };

  const updatePartRow = (index, field, value) => {
    setFormData((f) => {
      const next = [...f.parts];
      next[index] = { ...next[index], [field]: value };
      if (field === 'part_id' && value) {
        const part = parts.find((p) => String(p.id) === String(value));
        if (part) {
          next[index].part_number = part.part_number;
          next[index].part_name = part.part_description || part.description;
        }
      }
      return { ...f, parts: next };
    });
  };

  const removePartRow = (index) => {
    setFormData((f) => ({
      ...f,
      parts: f.parts.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    const name = formData.name?.trim();
    if (!name) {
      setError('Name is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        name,
        price: formData.price ? parseFloat(formData.price) : 0,
        quantity_on_hand: formData.quantity_on_hand !== '' && formData.quantity_on_hand != null ? parseInt(formData.quantity_on_hand, 10) : 0,
        parts: formData.parts
          .filter((p) => p.part_id)
          .map((p) => ({
            part_id: parseInt(p.part_id, 10),
            quantity: parseFloat(p.quantity) || 1
          }))
      };
      if (selectedProduct) {
        await productService.update(selectedProduct.id, payload);
      } else {
        await productService.create(payload);
      }
      setDialogOpen(false);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct?.id) return;
    try {
      setSaving(true);
      setError('');
      await productService.delete(selectedProduct.id);
      setDeleteConfirmOpen(false);
      setSelectedProduct(null);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete product');
    } finally {
      setSaving(false);
    }
  };

  const formatPartsSummary = (product) => {
    const count = product.parts_count ?? 0;
    return count === 0 ? '—' : `${count} part${count !== 1 ? 's' : ''}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Products
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedProduct && openEditDialog(selectedProduct)} disabled={!selectedProduct}>
            Edit
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => selectedProduct && setDeleteConfirmOpen(true)} disabled={!selectedProduct}>
            Delete
          </Button>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNewDialog}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Selected Product Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Name</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedProduct?.name ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Price</Typography>
            <Typography variant="body2">
              {selectedProduct ? formatCurrency(selectedProduct.price) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Qty on hand</Typography>
            <Typography variant="body2">
              {selectedProduct?.quantity_on_hand != null ? selectedProduct.quantity_on_hand : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Parts</Typography>
            <Typography variant="body2">
              {selectedProduct ? formatPartsSummary(selectedProduct) : '—'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSelectedProduct(null)}
            disabled={!selectedProduct}
            sx={{ ml: 'auto' }}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.name} onResizeStart={(e) => startResize('name', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Name</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.price} onResizeStart={(e) => startResize('price', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Price</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.qtyOnHand} onResizeStart={(e) => startResize('qtyOnHand', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Qty on hand</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.parts} onResizeStart={(e) => startResize('parts', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Parts</Typography></ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No products found. Use New to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              products.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedProduct?.id === row.id}
                  onClick={() => setSelectedProduct(row)}
                  onDoubleClick={() => openEditDialog(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.name }}>{row.name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.price }}>{formatCurrency(row.price)}</TableCell>
                  <TableCell sx={{ width: colWidths.qtyOnHand }}>{row.quantity_on_hand != null ? Number(row.quantity_on_hand) : '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.parts }}>{formatPartsSummary(row)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedProduct ? 'Edit Product' : 'New Product'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Name" value={formData.name} onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CurrencyTextField fullWidth size="small" label="Price" value={formData.price} onChange={(e) => setFormData((f) => ({ ...f, price: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Quantity on hand" type="number" value={formData.quantity_on_hand} onChange={(e) => setFormData((f) => ({ ...f, quantity_on_hand: e.target.value }))} inputProps={{ min: 0, step: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Parts (composed of)</Typography>
              {formData.parts.length === 0 && (
                <Button size="small" startIcon={<AddIcon />} onClick={addPartRow}>
                  Add part
                </Button>
              )}
              {formData.parts.length > 0 && (
                <>
                  <Table size="small" sx={{ mb: 0.5 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Part</TableCell>
                        <TableCell align="right" width={100}>Qty</TableCell>
                        <TableCell width={48} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.parts.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={row.part_id || ''}
                                displayEmpty
                                onChange={(e) => updatePartRow(idx, 'part_id', e.target.value)}
                                renderValue={(v) => {
                                  if (!v) return '— Select —';
                                  const p = parts.find((x) => String(x.id) === String(v));
                                  return p ? `${p.part_number || p.name || v}` : v;
                                }}
                              >
                                <MenuItem value="">— Select —</MenuItem>
                                {parts.map((p) => (
                                  <MenuItem key={p.id} value={String(p.id)}>
                                    {p.part_number ?? p.name ?? p.id} {p.name ? `(${p.name})` : ''}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={row.quantity ?? 1}
                              onChange={(e) => updatePartRow(idx, 'quantity', e.target.value)}
                              inputProps={{ min: 0.001, step: 0.001 }}
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removePartRow(idx)}><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button size="small" startIcon={<AddIcon />} onClick={addPartRow}>
                    Add part
                  </Button>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>Delete product <strong>{selectedProduct?.name ?? selectedProduct?.id}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
